"use strict";
/**
 * Role Hierarchy Utilities
 *
 * Single source of truth for the Valutten role hierarchy.
 * Used for invitation validation, status management, and role change authorization.
 *
 * Hierarchy: founder > super_admin > administrator > office_admin > credit_analyst > broker > marketing > broker_assistant > loyalty_client
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MFA_STAFF_OPERATOR_ROLES = exports.MFA_EXEMPT_ROLE = exports.ROLES_ENFORCING_HIDDEN_COMMISSIONS = exports.BROKER_FAMILY_ROLES = exports.ROLE_HIERARCHY = void 0;
exports.isBrokerFamily = isBrokerFamily;
exports.roleEnforcesHiddenCommissions = roleEnforcesHiddenCommissions;
exports.isFounderCaller = isFounderCaller;
exports.getAssignableRoles = getAssignableRoles;
exports.canManageRole = canManageRole;
exports.classifyMfaEnforcementRole = classifyMfaEnforcementRole;
/**
 * Numeric privilege levels. Higher = more privileged.
 */
exports.ROLE_HIERARCHY = {
    founder: 50,
    super_admin: 40,
    administrator: 30,
    office_admin: 20,
    credit_analyst: 11,
    broker: 10,
    marketing: 8,
    broker_assistant: 5,
    service_staff: 4,
    loyalty_client: 0,
};
/**
 * Roles that share broker-scoped data access.
 * credit_analyst is broker-family: it has its own broker doc + brokerDocId claim
 * and inherits scope from optionally-linked brokers via linkedBrokerDocIds.
 */
exports.BROKER_FAMILY_ROLES = ['broker', 'broker_assistant', 'credit_analyst'];
/**
 * Returns true if the role is in the broker family.
 * Members share the broker data scope resolved by resolveBrokerScope().
 */
function isBrokerFamily(role) {
    return exports.BROKER_FAMILY_ROLES.includes(role);
}
/**
 * Roles whose commission posture is DENY-BY-DEFAULT: `hideCommissions` is forced true
 * wherever the role is assigned, and no surface may clear it while the role holds.
 *
 * This lived as a bare `=== 'marketing'` in nine places (updateUserRole, onUserWrite x2,
 * createInvitation, acceptInvitation x2, createAccountAndAcceptInvitation x2,
 * toggleCommissionVisibility), so adding a second such role meant finding all of them and
 * missing one meant a silent hole. Named here so the concept has one home.
 */
exports.ROLES_ENFORCING_HIDDEN_COMMISSIONS = ['marketing', 'service_staff'];
/** True when the role hides commissions by design and no caller may reveal them. */
function roleEnforcesHiddenCommissions(role) {
    return exports.ROLES_ENFORCING_HIDDEN_COMMISSIONS.includes(role);
}
/**
 * Roles a founder may assign. Narrower than super_admin's list on purpose:
 * credit_analyst and marketing carry per-tenant linkage/entitlement configuration
 * that belongs to the subscriber, not to the platform operator.
 */
const FOUNDER_ASSIGNABLE_ROLES = [
    'super_admin',
    'administrator',
    'office_admin',
    'broker',
    'broker_assistant',
];
/**
 * SECURITY: `isFounder` is an IDENTITY flag, not a capability gate.
 *
 * It only widens authority when the caller's ROLE corroborates it. Until 2026-08-15 both
 * functions below short-circuited on the bare flag without reading `callerRole`, so an
 * `isFounder: true` claim sitting on an office_admin, a broker, or an empty role returned
 * the founder's assignable list (which includes `super_admin`) and an unconditional
 * `canManageRole` pass. Root CLAUDE.md §7 states the `founder` role does not exist in the
 * client app, so that combination should never occur, but the code did not defend it.
 *
 * Every writer of the flag (`scripts/bootstrap-founder.ts`, admin-functions
 * `setFounderClaim`, and the `__platform_harness__` claims in
 * admin-functions `verification/invokeClientCallable.ts`) sets `role: 'founder'` in the
 * SAME claim object, so requiring the pair costs no legitimate caller anything.
 */
function isFounderCaller(callerRole, callerIsFounder) {
    return callerIsFounder === true && callerRole === 'founder';
}
/**
 * Privilege level for a role, or `undefined` when the role is not in the hierarchy.
 *
 * The distinction matters: `loyalty_client` is legitimately level 0, so neither
 * `ROLE_HIERARCHY[role] || 0` nor `?? 0` can tell a real level-0 role apart from an
 * unrecognised one. Both operators map an unknown role to 0, which makes an unknown
 * TARGET the lowest-privilege user in the system, so every known caller outranks it:
 * a `broker` could manage a user whose role claim read `Administrator` (capital A) or
 * any other typo. Callers were already safe by accident (level 0 beats nothing), but
 * targets were not. Own-property lookup so inherited Object members (`toString`,
 * `constructor`) cannot masquerade as a level either.
 */
function roleLevel(role) {
    return Object.prototype.hasOwnProperty.call(exports.ROLE_HIERARCHY, role)
        ? exports.ROLE_HIERARCHY[role]
        : undefined;
}
/**
 * Returns roles that the caller is allowed to assign/invite.
 *
 * Standard rule: A role can assign roles strictly BELOW itself.
 * Exception: office_admin can also invite a peer office_admin (per business requirement);
 * it CANNOT invite administrator.
 *
 * credit_analyst and marketing are configured by subscriber (super_admin) and administrator only.
 * office_admin is intentionally excluded from inviting these new roles to keep linkage configuration
 * centralized at the subscriber/administrator level.
 *
 * `callerIsFounder` widens the list ONLY when `callerRole` is `founder` (see isFounderCaller);
 * on any other role the flag is ignored and the role's own list applies.
 *
 * @param callerRole - The caller's role
 * @param callerIsFounder - Whether the caller has the isFounder flag
 * @returns Array of roles the caller can assign
 */
function getAssignableRoles(callerRole, callerIsFounder) {
    if (isFounderCaller(callerRole, callerIsFounder)) {
        return [...FOUNDER_ASSIGNABLE_ROLES];
    }
    switch (callerRole) {
        // NOTE: no `case 'founder'`. The founder list requires BOTH signals (role AND flag);
        // a `founder` role arriving without the flag falls through to `default: []` (fail closed).
        case 'super_admin':
            return ['super_admin', 'administrator', 'office_admin', 'credit_analyst', 'broker', 'marketing', 'broker_assistant'];
        case 'administrator':
            return ['office_admin', 'credit_analyst', 'broker', 'marketing', 'broker_assistant'];
        case 'office_admin':
            // Strict hierarchy: office_admin can only invite roles at or below their level.
            // credit_analyst and marketing are intentionally excluded — those roles' linkage
            // and access scope must be configured by super_admin or administrator.
            return ['broker', 'broker_assistant', 'office_admin'];
        default:
            return [];
    }
}
/**
 * Returns true if the caller outranks the target in the hierarchy.
 * Used for status changes (revoke/restore) and role modifications.
 *
 * `callerIsFounder` grants the blanket pass ONLY when `callerRole` is `founder`
 * (see isFounderCaller); on any other role the flag is ignored and the numeric
 * comparison decides.
 *
 * Fails closed on an unrecognised role on EITHER side (see roleLevel).
 *
 * @param callerRole - The caller's role
 * @param callerIsFounder - Whether the caller has the isFounder flag
 * @param targetRole - The target user's role
 * @returns true if callerRole outranks targetRole and both roles are known
 */
function canManageRole(callerRole, callerIsFounder, targetRole) {
    if (isFounderCaller(callerRole, callerIsFounder))
        return true;
    // A `founder` ROLE without the corroborating flag fails closed, exactly as
    // getAssignableRoles does (no `case 'founder'` in its switch). Without this the numeric
    // path below would hand it level 50 and let it manage everyone, so the two functions
    // would disagree about what a half-formed founder claim is worth: "requires BOTH signals"
    // has to mean the same thing in both. Not exploitable today (every writer sets the pair),
    // which is precisely why the inconsistency is worth removing before it becomes load-bearing.
    if (callerRole === 'founder')
        return false;
    const callerLevel = roleLevel(callerRole);
    const targetLevel = roleLevel(targetRole);
    // An unknown role carries no privilege AND confers no manageability. Treating an
    // unrecognised target as level 0 (what the previous `|| 0` did) let any known caller
    // manage a user whose role string was corrupt or misspelled.
    //
    // REMEDIATION, deliberate: a user whose stored role is a non-empty unrecognised string
    // therefore cannot be re-roled or revoked from the client app by ANY role, because
    // `founder` does not exist there (root CLAUDE.md §7). Both call sites default a MISSING
    // role to 'broker' (`updateUserRole.ts`, `setUserStatus.ts`), so only genuine corruption
    // reaches this branch, and the repair path is the admin portal / a founder-run script
    // that rewrites the role claim directly. Widening this to let super_admin manage an
    // unknown-role target would be strictly de-escalating and is a defensible alternative,
    // but it is a policy call, not a code cleanup: raise it before changing this line.
    if (callerLevel === undefined || targetLevel === undefined)
        return false;
    return callerLevel > targetLevel;
}
/**
 * The ONLY role exempt from MFA enforcement (plan-v5 §3 founder ruling 1): the
 * subscribers' end customers, who have no enrolment surface. Every other role
 * in the union is in scope. Narrowing scope further requires the §6 D2
 * condition (consumer-facing, no privileged access, different recovery model).
 */
exports.MFA_EXEMPT_ROLE = 'loyalty_client';
/**
 * The staff/operator roles in MFA enforcement scope = the canonical union minus
 * {@link MFA_EXEMPT_ROLE}. Derived from {@link ROLE_HIERARCHY} keys so it tracks
 * the union automatically (a new role is in scope by default — the safe
 * direction). `founder` is included for completeness; the founder family is
 * additionally always-enforced in the blocking-function body regardless of
 * per-tenant config.
 */
exports.MFA_STAFF_OPERATOR_ROLES = Object.keys(exports.ROLE_HIERARCHY).filter((r) => r !== exports.MFA_EXEMPT_ROLE);
/** Every role in the canonical union, as a runtime set (drift-guarded by test). */
const KNOWN_ROLES = new Set(Object.keys(exports.ROLE_HIERARCHY));
/**
 * Classify a `role` claim for MFA enforcement. Pure; no I/O. See
 * {@link MfaRoleClass} for the four outcomes and their fail directions.
 */
function classifyMfaEnforcementRole(role) {
    if (role === undefined || role === null || role === '')
        return 'absent';
    if (role === exports.MFA_EXEMPT_ROLE)
        return 'exempt';
    if (KNOWN_ROLES.has(role))
        return 'in_scope';
    return 'unknown';
}
